'use client';

import { useParams } from 'next/navigation';
import { useSuspenseQuery } from '@apollo/client/react';
import { makeStyles } from 'tss-react/mui';
import Avatar from '@mui/material/Avatar';
import { groupPostQuery } from '@/apollo/groups';
import { momentParse } from '@/utils/time';
import MaterialLink from '@/components/material-link';
import MaterialLinkButton from '@/components/material-link-button';
import Divider from '@mui/material/Divider';
import Markdown from '@/components/markdown';
import SectionHeader from '@/components/section-header';
import CommentList from '@/components/groups/comment-list';
import NewComment from '@/components/groups/new-comment';
import NotificationPreferenceToggle from '@/components/groups/post-member-notification-preference-toggle';

const useStyles = makeStyles()((theme) => ({
  meta: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 12,
    color: theme.palette.text.primary
  },
  user: {
    display: 'flex',
    alignItems: 'center',
    marginLeft: theme.spacing(0.5)
  },
  avatar: {
    height: 18,
    width: 18
  },
  displayName: {
    marginLeft: theme.spacing(0.5)
  },
  body: {
    '& > *:first-child': {
      marginTop: 0,
    },
  },
  seeAllButton: {
    padding: theme.spacing(0, 1)
  },
  commentList: {
    margin: theme.spacing(2, 0)
  },
  editButton: {
    margin: theme.spacing(2, 0, 0, 0)
  },
}));

const GroupPostDetailPageClient = () => {
  const { id } = useParams();
  const { classes } = useStyles();

  const { data } = useSuspenseQuery(groupPostQuery, {
    variables: { id }
  });
  const groupPost = data?.groupPost;

  const { group } = groupPost;
  const createdAt = momentParse(groupPost.createdAt);

  return (
    <>
      <SectionHeader
        action={
          <MaterialLinkButton
            href={`/groups/${group.slug}/posts`}
            className={classes.seeAllButton}
            variant="outlined"
          >
            All Posts
          </MaterialLinkButton>
        }
      >
        {groupPost.subject}
      </SectionHeader>

      <div className={classes.body}>
        <Markdown>{groupPost.body}</Markdown>
      </div>

      <div className={classes.meta}>
        <span>
          Posted{' '}
          <time
            dateTime={createdAt.toString()}
            title={createdAt.format('dddd, MMMM Do, YYYY h:mm a')}
          >
            <MaterialLink
              href={`/groups/${group.slug}/posts/${groupPost.id}`}
            >
              {createdAt.fromNow()}
            </MaterialLink>
          </time>
          {' by '}
        </span>
        <span className={classes.user}>
          <MaterialLink
            href={`/people/${groupPost.user.username}`}
          >
            <Avatar
              alt={groupPost.user.displayName}
              src={groupPost.user.avatar.small.url}
              className={classes.avatar}
            />
          </MaterialLink>
          <MaterialLink
            href={`/people/${groupPost.user.username}`}
            className={classes.displayName}
          >
            {groupPost.user.displayName}
          </MaterialLink>
        </span>
      </div>

      {groupPost.permissions.editable && (
        <MaterialLinkButton
          href={`/groups/${group.slug}/posts/${groupPost.id}/edit`}
          variant="outlined"
          className={classes.editButton}
        >
          Edit Post
        </MaterialLinkButton>
      )}

      <Divider sx={{ my: 3 }} />

      <SectionHeader
        sx={{ mb: 1 }}
        action={<NotificationPreferenceToggle groupPost={groupPost} />}
      >
        Comments
      </SectionHeader>
      <div className={classes.commentList}>
        <CommentList groupPost={groupPost} />
      </div>
      <div>
        <NewComment groupPost={groupPost} />
      </div>
    </>
  );
};

export default GroupPostDetailPageClient;
